"use client";

import { c as _c } from "react/compiler-runtime";
import { addDays, isSameMonth, startOfMonth, startOfToday } from "date-fns";
import { CalendarClock, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ErrorAlert from "@/src/components/error-alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Calendar } from "@/src/components/shadcn/ui/calendar";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { dateFromKey, dateKeyFromDate, groupSlotsByDay, type NormalizedSlot } from "@/src/lib/scheduling/slot-display";
import { cn } from "@/src/lib/shadcn-utils";
import { formatAbsoluteTime, formatRelativeDateHeading } from "@/src/lib/utils/date-and-phone-utils";
const SELECTED_DAY_CLASSNAME = "[&>button]:bg-foreground [&>button]:text-background [&>button]:hover:bg-foreground [&>button]:hover:text-background";
export interface AppointmentSlotPickerProps {
  /** All currently-loaded slots (already fetched by the parent). */
  slots: NormalizedSlot[];
  selectedSlotId: string | null;
  /** Fired with the chosen slot, or `null` when the selection is cleared (e.g. day change). */
  onSelectSlot: (slot: NormalizedSlot | null) => void;
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  emptyHint?: string;
  /** Optional windowed search controls (calendar auto-widens as the user pages forward). */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  canLoadMore?: boolean;
  windowDays?: number;
  maxWindowDays?: number;
  /** Staff surfaces flag ranked slots; shows a subtle "Not preferred" hint. */
  showPreferredBadge?: boolean;
  /** Secondary label shown below each time. Provider is the default; provider-first flows use location. */
  slotSecondaryLabel?: "provider" | "location";
  /**
   * When the only-one-provider "Appointments with …" summary should render. Surfaces that offer their
   * own provider control (e.g. the online flow's provider dropdown) pass `false` to avoid duplication.
   */
  showProviderSummary?: boolean;
}
export function AppointmentSlotPicker(t0) {
  const $ = _c(98);
  const {
    slots,
    selectedSlotId,
    onSelectSlot,
    isLoading: t1,
    error: t2,
    emptyMessage: t3,
    emptyHint: t4,
    onLoadMore,
    isLoadingMore: t5,
    canLoadMore: t6,
    windowDays,
    maxWindowDays,
    showPreferredBadge: t7,
    slotSecondaryLabel: t8,
    showProviderSummary: t9
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const error = t2 === undefined ? null : t2;
  const emptyMessage = t3 === undefined ? "No online appointments in this window" : t3;
  const emptyHint = t4 === undefined ? "Try searching further out, or call the clinic to find a time that works." : t4;
  const isLoadingMore = t5 === undefined ? false : t5;
  const canLoadMore = t6 === undefined ? false : t6;
  const showPreferredBadge = t7 === undefined ? false : t7;
  const slotSecondaryLabel = t8 === undefined ? "provider" : t8;
  const showProviderSummary = t9 === undefined ? true : t9;
  const today = startOfToday();
  let t10;
  if ($[0] !== slots) {
    t10 = groupSlotsByDay(slots);
    $[0] = slots;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const dayGroups = t10;
  let t11;
  if ($[2] !== dayGroups) {
    t11 = new Set(dayGroups.map(_temp));
    $[2] = dayGroups;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const availableDateKeys = t11;
  let t12;
  if ($[4] !== slots) {
    t12 = new Set(slots.map(_temp2));
    $[4] = slots;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  const distinctProviderCount = t12.size;
  const lastAvailableDate = dayGroups.length > 0 ? dayGroups[dayGroups.length - 1].date : undefined;
  let t13;
  if ($[6] !== maxWindowDays) {
    t13 = maxWindowDays !== undefined ? addDays(today, maxWindowDays) : undefined;
    $[6] = maxWindowDays;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const maxSearchDate = t13;
  let t14;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => today;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  const [visibleMonth, setVisibleMonth] = useState(t14);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  let t15;
  let t16;
  if ($[9] !== availableDateKeys || $[10] !== dayGroups || $[11] !== onSelectSlot || $[12] !== selectedDateKey || $[13] !== selectedSlotId || $[14] !== visibleMonth) {
    t15 = () => {
      if (dayGroups.length === 0) {
        if (selectedDateKey !== null) {
          setSelectedDateKey(null);
        }
        return;
      }
      const selectedValidOverall = selectedDateKey !== null && availableDateKeys.has(selectedDateKey);
      if (!selectedValidOverall) {
        const first = dayGroups[0];
        setSelectedDateKey(first.dateKey);
        setVisibleMonth(prev => isSameMonth(prev, first.date) ? prev : startOfMonth(first.date));
        return;
      }
      if (!isSameMonth(dateFromKey(selectedDateKey), visibleMonth)) {
        const firstInVisible = dayGroups.find(group_0 => isSameMonth(group_0.date, visibleMonth));
        if (firstInVisible) {
          setSelectedDateKey(firstInVisible.dateKey);
        }
        if (selectedSlotId) {
          onSelectSlot(null);
        }
      }
    };
    t16 = [dayGroups, visibleMonth, availableDateKeys, selectedDateKey, selectedSlotId, onSelectSlot];
    $[9] = availableDateKeys;
    $[10] = dayGroups;
    $[11] = onSelectSlot;
    $[12] = selectedDateKey;
    $[13] = selectedSlotId;
    $[14] = visibleMonth;
    $[15] = t15;
    $[16] = t16;
  } else {
    t15 = $[15];
    t16 = $[16];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[17] !== canLoadMore || $[18] !== isLoadingMore || $[19] !== onLoadMore || $[20] !== visibleMonth || $[21] !== windowDays) {
    t17 = () => {
      if (!canLoadMore || isLoadingMore || windowDays === undefined || !onLoadMore) {
        return;
      }
      const searchedThrough = addDays(today, windowDays);
      if (startOfMonth(visibleMonth).getTime() > searchedThrough.getTime()) {
        onLoadMore();
      }
    };
    t18 = [visibleMonth, windowDays, canLoadMore, isLoadingMore, onLoadMore, today];
    $[17] = canLoadMore;
    $[18] = isLoadingMore;
    $[19] = onLoadMore;
    $[20] = visibleMonth;
    $[21] = windowDays;
    $[22] = t17;
    $[23] = t18;
  } else {
    t17 = $[22];
    t18 = $[23];
  }
  useEffect(t17, t18);
  if (isLoading) {
    let t19;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Skeleton className="h-72 w-full max-w-[320px] flex-[1_1_280px] rounded-lg" />;
      $[24] = t19;
    } else {
      t19 = $[24];
    }
    let t20;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Skeleton className="h-6 w-40" />;
      $[25] = t20;
    } else {
      t20 = $[25];
    }
    let t21;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <div className="flex flex-wrap gap-6">{t19}<div className="min-w-[280px] flex-[1_1_280px] space-y-3">{t20}<div className="grid grid-cols-3 gap-2 sm:grid-cols-4">{Array.from({
              length: 8
            }).map(_temp3)}</div></div></div>;
      $[26] = t21;
    } else {
      t21 = $[26];
    }
    return t21;
  }
  if (error) {
    let t19;
    if ($[27] !== error) {
      t19 = <ErrorAlert compact={true} message={error} />;
      $[27] = error;
      $[28] = t19;
    } else {
      t19 = $[28];
    }
    return t19;
  }
  if (dayGroups.length === 0) {
    let t19;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <CalendarClock className="h-8 w-8 text-muted-foreground" />;
      $[29] = t19;
    } else {
      t19 = $[29];
    }
    let t20;
    if ($[30] !== emptyMessage) {
      t20 = <p className="font-medium text-foreground">{emptyMessage}</p>;
      $[30] = emptyMessage;
      $[31] = t20;
    } else {
      t20 = $[31];
    }
    let t21;
    if ($[32] !== emptyHint) {
      t21 = <p className="text-sm text-muted-foreground">{emptyHint}</p>;
      $[32] = emptyHint;
      $[33] = t21;
    } else {
      t21 = $[33];
    }
    let t22;
    if ($[34] !== t20 || $[35] !== t21) {
      t22 = <div>{t20}{t21}</div>;
      $[34] = t20;
      $[35] = t21;
      $[36] = t22;
    } else {
      t22 = $[36];
    }
    let t23;
    if ($[37] !== canLoadMore || $[38] !== isLoadingMore || $[39] !== onLoadMore) {
      t23 = canLoadMore && onLoadMore && <Button variant="outline" onClick={onLoadMore} disabled={isLoadingMore}>{isLoadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Search later dates</Button>;
      $[37] = canLoadMore;
      $[38] = isLoadingMore;
      $[39] = onLoadMore;
      $[40] = t23;
    } else {
      t23 = $[40];
    }
    let t24;
    if ($[41] !== t22 || $[42] !== t23) {
      t24 = <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed p-8 text-center">{t19}{t22}{t23}</div>;
      $[41] = t22;
      $[42] = t23;
      $[43] = t24;
    } else {
      t24 = $[43];
    }
    return t24;
  }
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[44] !== availableDateKeys || $[45] !== canLoadMore || $[46] !== dayGroups || $[47] !== distinctProviderCount || $[48] !== isLoadingMore || $[49] !== lastAvailableDate || $[50] !== maxSearchDate || $[51] !== onSelectSlot || $[52] !== selectedDateKey || $[53] !== selectedSlotId || $[54] !== showPreferredBadge || $[55] !== showProviderSummary || $[56] !== slotSecondaryLabel || $[57] !== slots[0] || $[58] !== visibleMonth) {
    const selectedDay = dayGroups.find(group_1 => group_1.dateKey === selectedDateKey) ?? null;
    const selectedInVisibleMonth = selectedDay ? isSameMonth(selectedDay.date, visibleMonth) : false;
    let t24;
    if ($[64] !== visibleMonth) {
      t24 = visibleMonth.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric"
      });
      $[64] = visibleMonth;
      $[65] = t24;
    } else {
      t24 = $[65];
    }
    const visibleMonthLabel = t24;
    let t25;
    if ($[66] !== onSelectSlot || $[67] !== selectedSlotId) {
      t25 = dateKey => {
        setSelectedDateKey(dateKey);
        if (selectedSlotId) {
          onSelectSlot(null);
        }
      };
      $[66] = onSelectSlot;
      $[67] = selectedSlotId;
      $[68] = t25;
    } else {
      t25 = $[68];
    }
    const handleSelectDate = t25;
    t22 = "flex flex-wrap gap-6";
    let t26;
    if ($[69] !== selectedDateKey) {
      t26 = selectedDateKey ? dateFromKey(selectedDateKey) : undefined;
      $[69] = selectedDateKey;
      $[70] = t26;
    } else {
      t26 = $[70];
    }
    let t27;
    if ($[71] !== handleSelectDate) {
      t27 = date => date && handleSelectDate(dateKeyFromDate(date));
      $[71] = handleSelectDate;
      $[72] = t27;
    } else {
      t27 = $[72];
    }
    const t28 = canLoadMore ? maxSearchDate : lastAvailableDate;
    let t29;
    if ($[73] !== availableDateKeys) {
      t29 = date_0 => date_0 < today || !availableDateKeys.has(dateKeyFromDate(date_0));
      $[73] = availableDateKeys;
      $[74] = t29;
    } else {
      t29 = $[74];
    }
    let t30;
    if ($[75] !== t26 || $[76] !== t27 || $[77] !== t28 || $[78] !== t29 || $[79] !== visibleMonth) {
      t30 = <Calendar mode="single" month={visibleMonth} onMonthChange={setVisibleMonth} selected={t26} onSelect={t27} startMonth={today} endMonth={t28} disabled={t29} selectedClassName={SELECTED_DAY_CLASSNAME} highlightToday={true} />;
      $[75] = t26;
      $[76] = t27;
      $[77] = t28;
      $[78] = t29;
      $[79] = visibleMonth;
      $[80] = t30;
    } else {
      t30 = $[80];
    }
    let t31;
    if ($[81] !== isLoadingMore) {
      t31 = isLoadingMore && <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[1px]"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
      $[81] = isLoadingMore;
      $[82] = t31;
    } else {
      t31 = $[82];
    }
    if ($[83] !== t30 || $[84] !== t31) {
      t23 = <div className="relative mx-auto flex w-full max-w-[320px] flex-[1_1_280px] justify-center rounded-lg border bg-card p-2">{t30}{t31}</div>;
      $[83] = t30;
      $[84] = t31;
      $[85] = t23;
    } else {
      t23 = $[85];
    }
    t19 = "min-w-[280px] flex-[1_1_280px] space-y-4";
    if ($[86] !== distinctProviderCount || $[87] !== showProviderSummary || $[88] !== slots[0]) {
      t20 = showProviderSummary && distinctProviderCount === 1 && slots[0]?.providerDisplayName && <p className="text-sm text-muted-foreground">Appointments with <span className="font-medium text-foreground">{slots[0].providerDisplayName}</span></p>;
      $[86] = distinctProviderCount;
      $[87] = showProviderSummary;
      $[88] = slots[0];
      $[89] = t20;
    } else {
      t20 = $[89];
    }
    t21 = selectedInVisibleMonth && selectedDay ? <><h3 className="text-base font-semibold text-foreground">{formatRelativeDateHeading(selectedDay.dateKey)}</h3><div className={cn("grid gap-2", slotSecondaryLabel === "location" ? "grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))]" : "grid-cols-[repeat(auto-fill,minmax(5rem,1fr))]")}>{selectedDay.slots.map(slot_0 => {
          const isSelected = selectedSlotId === slot_0.id;
          const showNotPreferred = showPreferredBadge && slot_0.isPreferred === false;
          const secondaryLabel = slotSecondaryLabel === "location" ? slot_0.locationDisplayName : distinctProviderCount > 1 ? slot_0.providerDisplayName : null;
          return <button key={slot_0.id} type="button" onClick={() => onSelectSlot(slot_0)} aria-pressed={isSelected} className={cn("flex flex-col items-center justify-center rounded-md border px-2 py-2 text-sm transition-all", "hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", isSelected ? "border-foreground bg-foreground text-background" : "border-border bg-background text-foreground")}><span className="whitespace-nowrap font-medium">{formatAbsoluteTime(slot_0.start)}</span>{secondaryLabel && <span title={secondaryLabel} className={cn("mt-0.5 max-w-full truncate text-[11px]", isSelected ? "text-background/80" : "text-muted-foreground")}>{secondaryLabel}</span>}{showNotPreferred && <span className={cn("mt-0.5 text-[10px] uppercase tracking-wide", isSelected ? "text-background/70" : "text-muted-foreground/70")}>Not preferred</span>}</button>;
        })}</div></> : isLoadingMore ? <div className="flex items-center gap-2 rounded-lg border border-dashed p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Finding more openings…</div> : <div className="rounded-lg border border-dashed p-6 text-center"><p className="text-sm font-medium text-foreground">No openings in {visibleMonthLabel}</p><p className="mt-1 text-sm text-muted-foreground">Use the calendar arrows to check another month.</p></div>;
    $[44] = availableDateKeys;
    $[45] = canLoadMore;
    $[46] = dayGroups;
    $[47] = distinctProviderCount;
    $[48] = isLoadingMore;
    $[49] = lastAvailableDate;
    $[50] = maxSearchDate;
    $[51] = onSelectSlot;
    $[52] = selectedDateKey;
    $[53] = selectedSlotId;
    $[54] = showPreferredBadge;
    $[55] = showProviderSummary;
    $[56] = slotSecondaryLabel;
    $[57] = slots[0];
    $[58] = visibleMonth;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t19 = $[59];
    t20 = $[60];
    t21 = $[61];
    t22 = $[62];
    t23 = $[63];
  }
  let t24;
  if ($[90] !== t19 || $[91] !== t20 || $[92] !== t21) {
    t24 = <div className={t19}>{t20}{t21}</div>;
    $[90] = t19;
    $[91] = t20;
    $[92] = t21;
    $[93] = t24;
  } else {
    t24 = $[93];
  }
  let t25;
  if ($[94] !== t22 || $[95] !== t23 || $[96] !== t24) {
    t25 = <div className={t22}>{t23}{t24}</div>;
    $[94] = t22;
    $[95] = t23;
    $[96] = t24;
    $[97] = t25;
  } else {
    t25 = $[97];
  }
  return t25;
}
function _temp3(_, index) {
  return <Skeleton key={index} className="h-10 w-full rounded-md" />;
}
function _temp2(slot) {
  return slot.providerDisplayName ?? "";
}
function _temp(group) {
  return group.dateKey;
}